import React, {FunctionComponent, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import store from "@/store";
import {useSnapshot} from "valtio";

import useTaskManager from "@/hooks/useTaskManager";
import {ProductsFilter,} from '@/types/product'


import SearchBar, {SearchBarOptions} from "@/components/UiElements/SearchBar";
import Pagination, {PaginationRef} from "@/components/UiElements/Pagination";
import getAllProducts from "@/services/ProductService/getAllProducts";
import Splash from "@/components/splash/Splash";
import ProductListItem from "@/app/products/ProductListItem";
import syncShopifyProducts from "@/services/ProductService/syncShopifyProducts";
import getResultSyncShopifyProducts from "@/services/ProductService/getResultSyncShopifyProducts";


type ProductListViewProps = {}
const perPage = 40;


const ProductListView: FunctionComponent<ProductListViewProps> = () => {
    const [filter, setFilter] = useState<ProductsFilter>({
        text: '',
        withProfile: true,
        withoutProfile: true,
        bundles: true,
        onlyActive: true,
    });

    const {products, companySetting} = useSnapshot(store);
    const { startTask, taskInProgress } = useTaskManager();


    const [loading, setLoading] = useState(true);

    const paginationRef = React.useRef<PaginationRef>(null);
    const isInitialMount = useRef(true);


    const syncProducts = async (): Promise<void> => {

        await syncShopifyProducts();

        return new Promise<void>((resolve, reject) => {
            const interval = setInterval(async () => {
                try {
                    const data = await getResultSyncShopifyProducts();

                    if (data.result) {
                        clearInterval(interval);
                        await getAllProducts()
                        await localStorage.setItem('lastSynced', new Date().toUTCString());
                        resolve();
                    }

                    if (data === "No sync operation has not been started yet") {
                        clearInterval(interval);
                        reject(new Error("Sync operation not started."));
                    }

                } catch (error) {
                    clearInterval(interval);
                    reject(error);
                }
            }, 1000);
        });
    };


    const filteredProducts = useMemo(() => {
        return products.filter((product) => {
            const string = `"${product.sku.toLowerCase()}" "${product.name.toLowerCase()}"`;
            const text = filter.text.toLowerCase();
            return string.includes(text);
        }).filter((product) => {
            if (filter.onlyActive && product.commerce_status !== "active") return false;
            if (product.is_product_profile_active) {
                if (filter.withProfile && product.product_profile_id) return true;
                if (filter.withoutProfile && !product.product_profile_id) return true;
            } else {
                if (filter.onlyActive && product.commerce_status === "active") return true;
                if (filter.bundles && !product.is_product_profile_active) return true;
            }
            return false;
        });
    }, [filter, products]);

    const total = useMemo(() => Math.ceil(filteredProducts.length / perPage), [filteredProducts]);

    const handleGetPageData = useCallback((pageNumber: number) => {
        const start = (pageNumber - 1) * perPage;
        const end = start + perPage;
        return filteredProducts.slice(start, end);
    }, [filteredProducts]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        paginationRef.current?.reset();
    }, [filteredProducts]);


    const handleOnSearch = (text: string) => {
        setFilter({
            ...filter,
            text: text
        });
    }

    useEffect(() => {

        if (!products.length) {
            setLoading(true);
            getAllProducts().then(() => {
                setLoading(false);
            })
        } else {
            setLoading(false);
        }

    }, []);

    const productListSearchFilters = [
            {
                label: 'Bundles',
                onClick: (value: boolean) => {
                    setFilter(prevState => ({
                        ...prevState,
                        bundles: value
                    }))
                },
                count: products.filter((product) => product.is_bundle).length,
                default: filter.bundles
            },
            {
                label: 'With Profile',
                onClick: (value: boolean) => {
                    setFilter(prevState => ({
                        ...prevState,
                        withProfile: value
                    }))
                },
                count: products.filter((product) => product.product_profile_id).length,
                default: filter.withProfile
            },
            {
                label: 'Without Profile',
                onClick: (value: boolean) => {
                    setFilter(prevState => ({
                        ...prevState,
                        withoutProfile: value
                    }))
                },
                default: filter.withoutProfile,
                count: products.filter((product) => !product.product_profile_id && !product.is_bundle).length,
            },
            {
                label: 'Only Active',
                onClick: (value: boolean) => {
                    setFilter(prevState => ({
                        ...prevState,
                        onlyActive: value
                    }))
                },
                default: filter.onlyActive,
                count: products.filter((product) => product.commerce_status === "active").length,
            }



    ]


    const productListSearchOptions:SearchBarOptions[] = companySetting?.profile_version === 'v2' ? [] : [
        {
            label: (
                <span className="flex flex-row gap-md">
                        <span className={`font-icon-solid text-primary ${taskInProgress?.['syncShopify'] ? "animate-spin" : ""}`}>
                            {taskInProgress?.['syncShopify'] ? "" : "" }
                        </span>
                        Sync with Store
                    </span>
            ),
            onClick: () => {
                startTask('syncShopify', syncProducts)
            },
            disabled: taskInProgress?.['syncShopify']
        }
    ]




    return (
        <div className={"min-h-[100%] relative"}>

            <div className={"sticky top-[48px] bg-white  z-[10]"}>
                <div className={"h-[48px] absolute top-[-48px]  w-full bg-white z-[-1]"}></div>
                <SearchBar
                    placeholder={"Search by category name"}
                    filters={productListSearchFilters}
                    options={productListSearchOptions}
                    onChange={handleOnSearch}
                    searchText={filter.text}
                />
            </div>
            {loading &&
                <Splash/>
            }
            {(filteredProducts.length > 0 && total > 0) ?
                <Pagination
                    ref={paginationRef}
                    theme={(data) => {
                        return (
                            <ProductListItem product={data}/>
                        )
                    }}
                    total={total}
                    offset={150}
                    getPageData={handleGetPageData}
                    className={"pt-xxl"}
                />
                :
                <div className={"flex flex-row justify-center items-center px-mx py-xl text-gray-300  bg-gray-50 rounded-md mt-xxl gap-lg h-[150px]"}>
                    <p>
                        <span>No results match your current filters. Try </span>
                        <span
                            className={"text-gray-300 underline cursor-pointer hover:text-gray-900 transition-all"}
                            onClick={() => setFilter({
                                text: '',
                                withProfile: true,
                                withoutProfile: true,
                                bundles: true,
                                onlyActive: false,
                            })}
                        >resetting some filters</span>
                        <span> to see more options.</span>
                    </p>
                </div>
            }


        </div>
    )
}

export default ProductListView
